import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { DeployListItem, DeploySleeveOverview } from '../services/deployListApi';
import { DeployTargetWeightCell } from './DeployTargetWeightCell';

function money(n: number | null | undefined, digits = 2): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })}`;
}

function pct(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)}%`;
}

function pctTone(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n) || n === 0) return 'text-kib-muted';
  return n > 0 ? 'text-emerald-400' : 'text-red-400';
}

function WeightBar({ value, max = 10 }: { value: number | null; max?: number }) {
  const v = value != null && Number.isFinite(value) ? Math.max(0, value) : 0;
  const width = Math.min(100, (v / Math.max(max, 1)) * 100);
  return (
    <div className="mt-1 h-1 w-16 overflow-hidden rounded-full bg-white/[0.08]">
      <div className="h-full rounded-full bg-kib-cyber/80" style={{ width: `${width}%` }} />
    </div>
  );
}

export const DeployListPanel: React.FC<{
  items: DeployListItem[];
  totalTargetWeightPct: number;
  sleeve: DeploySleeveOverview | null;
  loading: boolean;
  optimizing: boolean;
  recomputing?: boolean;
  onOptimize: () => void;
  onRecomputeIdeal?: () => void;
  onClear: () => void;
  onRemove: (alertId: number) => void;
  onPatchWeight?: (
    alertId: number,
    input: { targetWeightPct?: number; weightPinned?: boolean }
  ) => Promise<void>;
}> = ({
  items,
  totalTargetWeightPct,
  sleeve,
  loading,
  optimizing,
  recomputing = false,
  onOptimize,
  onRecomputeIdeal,
  onClear,
  onRemove,
  onPatchWeight
}) => {
  const sorted = useMemo(
    () =>
      [...items].sort((a, b) => {
        const aw = a.targetWeightPct ?? -1;
        const bw = b.targetWeightPct ?? -1;
        if (bw !== aw) return bw - aw;
        return a.symbol.localeCompare(b.symbol);
      }),
    [items]
  );

  const missingWeights = items.filter((i) => i.targetWeightPct == null).length;
  const missingBands = items.filter(
    (i) => i.suggestedLimitMin == null || i.suggestedLimitMax == null
  ).length;
  const ownedCount = items.filter((i) => i.position && (i.position.qty ?? 0) > 0).length;
  const busy = loading || optimizing || recomputing;
  const maxWeight = Math.max(10, ...items.map((i) => i.targetWeightPct || 0));

  return (
    <section className="mt-6 overflow-hidden rounded-lg border border-white/[0.08] bg-kib-card shadow-soft">
      <div className="border-b border-white/[0.06] bg-kib-surface/90 px-4 py-4 sm:px-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 max-w-2xl">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-kib-fg">Deploy list</h3>
              {sleeve?.live ? (
                <span
                  className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                    sleeve.live.isLive
                      ? 'bg-emerald-500/15 text-emerald-300'
                      : 'bg-white/[0.06] text-kib-muted'
                  }`}
                >
                  {sleeve.live.label}
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-xs leading-relaxed text-kib-muted sm:text-sm">
              Overview of the{' '}
              <strong className="text-kib-fg/90">Deploy-list Dip Sleeve</strong> — edit Target % for
              priority (Pin keeps it through auto-refresh). Risk knobs and go-live controls live on{' '}
              <Link
                to="/deploy-strategies"
                className="text-kib-cyber underline-offset-2 hover:underline"
              >
                Deploy Strategies
              </Link>
              .
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap">
            {onRecomputeIdeal ? (
              <button
                type="button"
                className="btn-secondary min-h-[44px] w-full text-sm disabled:opacity-50 sm:w-auto"
                disabled={busy || items.length === 0}
                onClick={onRecomputeIdeal}
              >
                {recomputing ? 'Recomputing…' : 'Recompute ideals'}
              </button>
            ) : null}
            <button
              type="button"
              className="btn-primary min-h-[44px] w-full text-sm disabled:opacity-50 sm:w-auto"
              disabled={busy}
              onClick={onOptimize}
            >
              {optimizing ? 'Optimizing…' : 'Optimize with Grok'}
            </button>
            {items.length > 0 ? (
              <button
                type="button"
                className="btn-secondary min-h-[44px] w-full text-sm disabled:opacity-50 sm:w-auto"
                disabled={busy}
                onClick={onClear}
              >
                Clear list
              </button>
            ) : null}
          </div>
        </div>

        {/* Sleeve performance strip */}
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-md border border-white/[0.06] bg-black/20 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-kib-muted">Paper equity</p>
            <p className="mt-0.5 font-mono text-sm font-semibold tabular-nums text-kib-fg">
              {money(sleeve?.equity)}
            </p>
            {sleeve?.accountHint ? (
              <p className="mt-0.5 text-[10px] text-kib-muted">Acct {sleeve.accountHint}</p>
            ) : (
              <p className="mt-0.5 text-[10px] text-kib-muted">
                {sleeve?.brokerageConnected ? 'Alpaca paper' : 'Connect Alpaca to track'}
              </p>
            )}
          </div>
          <div className="rounded-md border border-white/[0.06] bg-black/20 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-kib-muted">Day P&amp;L</p>
            <p className={`mt-0.5 font-mono text-sm font-semibold tabular-nums ${pctTone(sleeve?.dayPnlPct)}`}>
              {pct(sleeve?.dayPnlPct)}
            </p>
            <p className={`mt-0.5 text-[10px] tabular-nums ${pctTone(sleeve?.periodReturnPct)}`}>
              {sleeve?.periodLabel || '1M'} {pct(sleeve?.periodReturnPct)}
            </p>
          </div>
          <div className="rounded-md border border-white/[0.06] bg-black/20 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-kib-muted">Owned (paper)</p>
            <p className="mt-0.5 font-mono text-sm font-semibold tabular-nums text-kib-fg">
              {money(sleeve?.ownedMarketValue)}
            </p>
            <p className={`mt-0.5 text-[10px] tabular-nums ${pctTone(sleeve?.ownedUnrealizedPl)}`}>
              {ownedCount}/{items.length} names · uP/L {money(sleeve?.ownedUnrealizedPl)}
            </p>
          </div>
          <div className="rounded-md border border-white/[0.06] bg-black/20 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-kib-muted">Sleeve orders</p>
            <p className="mt-0.5 font-mono text-sm font-semibold tabular-nums text-kib-fg">
              {sleeve?.ordersFilled ?? 0}
              <span className="font-normal text-kib-muted"> filled</span>
            </p>
            <p className="mt-0.5 text-[10px] text-kib-muted">
              {sleeve?.ordersPlaced ?? 0} placed · {sleeve?.ordersSkipped ?? 0} skipped
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-kib-muted">
          <span>
            Target weight{' '}
            <span className="font-mono font-medium text-kib-fg">
              {totalTargetWeightPct.toFixed(1)}%
            </span>
            {items.length > 0 ? ` · ${items.length} name(s)` : ''}
          </span>
          {missingWeights > 0 || missingBands > 0 ? (
            <span className="text-amber-200/90">
              {missingWeights > 0 ? `${missingWeights} missing weight` : null}
              {missingWeights > 0 && missingBands > 0 ? ' · ' : null}
              {missingBands > 0 ? `${missingBands} missing limit band` : null}
              {onRecomputeIdeal
                ? ' — run Recompute ideals (also auto weekdays ~8:30 ET)'
                : null}
            </span>
          ) : null}
        </div>
      </div>

      <div className="px-4 py-4 sm:px-5">
        {loading && items.length === 0 ? (
          <p className="text-sm text-kib-muted">Loading deploy list…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-kib-muted">
            Check <strong className="text-kib-fg/90">Deploy</strong> on US watchlist rows, or run{' '}
            <strong className="text-kib-fg/90">Optimize with Grok</strong> to fill the list from ideal
            dips.
          </p>
        ) : (
          <>
            <ul className="space-y-2 md:hidden">
              {sorted.map((it) => {
                const owned = it.position && (it.position.qty ?? 0) > 0;
                return (
                  <li
                    key={it.id}
                    className="rounded-md border border-white/[0.06] bg-black/20 px-3 py-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <Link
                          to={`/charts?symbol=${encodeURIComponent(it.symbol)}`}
                          className="inline-flex min-h-[44px] items-center font-mono text-base font-semibold text-kib-fg hover:text-kib-cyber"
                          title={`Open Stock Charts for ${it.symbol}`}
                        >
                          {it.symbol}
                        </Link>
                        {it.baselinePrice != null ? (
                          <p className="text-[10px] tabular-nums text-kib-muted">
                            Base {money(it.baselinePrice)}
                          </p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="min-h-[44px] shrink-0 px-2 text-sm text-red-400/90 hover:text-red-300 hover:underline"
                        onClick={() => onRemove(it.userAlertId)}
                      >
                        Remove
                      </button>
                    </div>
                    <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                      <div>
                        <dt className="text-kib-muted">Target</dt>
                        <dd>
                          {onPatchWeight ? (
                            <DeployTargetWeightCell
                              symbol={it.symbol}
                              targetWeightPct={it.targetWeightPct}
                              weightPinned={Boolean(it.weightPinned)}
                              disabled={busy}
                              onSave={(input) => onPatchWeight(it.userAlertId, input)}
                            />
                          ) : (
                            <span className="font-mono tabular-nums text-kib-cyber">
                              {it.targetWeightPct != null ? `${it.targetWeightPct}%` : '—'}
                              {it.weightPinned ? (
                                <span className="ml-1 text-[9px] text-amber-200">pinned</span>
                              ) : null}
                            </span>
                          )}
                        </dd>
                        <WeightBar value={it.targetWeightPct} max={maxWeight} />
                      </div>
                      <div>
                        <dt className="text-kib-muted">Actual</dt>
                        <dd className="font-mono tabular-nums text-kib-fg">
                          {it.actualWeightPct != null ? `${it.actualWeightPct}%` : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-kib-muted">Owned</dt>
                        <dd className="font-mono tabular-nums text-kib-fg">
                          {owned ? money(it.position?.marketValue) : '—'}
                          {owned && it.position?.qty != null ? (
                            <span className="block text-[10px] text-kib-muted">
                              {it.position.qty} sh
                              {it.position?.avgEntryPrice != null
                                ? ` @ ${money(it.position.avgEntryPrice)}`
                                : ''}
                            </span>
                          ) : null}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-kib-muted">uP/L</dt>
                        <dd
                          className={`font-mono tabular-nums ${pctTone(it.position?.unrealizedPl)}`}
                        >
                          {owned ? money(it.position?.unrealizedPl) : '—'}
                          {owned && it.position?.unrealizedPlpc != null ? (
                            <span className="block text-[10px]">{pct(it.position.unrealizedPlpc)}</span>
                          ) : null}
                        </dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-kib-muted">Limit band</dt>
                        <dd className="font-mono tabular-nums text-kib-muted">
                          {it.suggestedLimitMin != null && it.suggestedLimitMax != null
                            ? `${money(it.suggestedLimitMin)} – ${money(it.suggestedLimitMax)}`
                            : '—'}
                        </dd>
                      </div>
                      {(it.grokRationale || it.source) && (
                        <div className="col-span-2">
                          <dt className="text-kib-muted">Note</dt>
                          <dd className="line-clamp-2 text-kib-muted">
                            {it.grokRationale ||
                              (it.source === 'manual' ? 'Added manually' : it.source || '—')}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </li>
                );
              })}
            </ul>
            <div className="hidden overflow-x-auto rounded-md border border-white/[0.06] md:block">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-kib-surface/80 text-[10px] uppercase tracking-wide text-kib-muted">
                  <tr>
                    <th className="px-3 py-2 text-left">Symbol</th>
                    <th className="px-3 py-2 text-right">Target %</th>
                    <th className="px-3 py-2 text-right">Owned</th>
                    <th className="px-3 py-2 text-right">Actual %</th>
                    <th className="px-3 py-2 text-right">uP/L</th>
                    <th className="px-3 py-2 text-left">Limit band</th>
                    <th className="px-3 py-2 text-left">Note</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {sorted.map((it) => {
                    const owned = it.position && (it.position.qty ?? 0) > 0;
                    return (
                      <tr key={it.id} className="hover:bg-white/[0.02]">
                        <td className="px-3 py-2.5">
                          <Link
                            to={`/charts?symbol=${encodeURIComponent(it.symbol)}`}
                            className="font-mono font-semibold text-kib-fg hover:text-kib-cyber"
                            title={`Open Stock Charts for ${it.symbol}`}
                          >
                            {it.symbol}
                          </Link>
                          {it.baselinePrice != null ? (
                            <p className="mt-0.5 text-[10px] tabular-nums text-kib-muted">
                              Base {money(it.baselinePrice)}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {onPatchWeight ? (
                            <div className="flex flex-col items-end">
                              <DeployTargetWeightCell
                                symbol={it.symbol}
                                targetWeightPct={it.targetWeightPct}
                                weightPinned={Boolean(it.weightPinned)}
                                disabled={busy}
                                onSave={(input) => onPatchWeight(it.userAlertId, input)}
                              />
                              <WeightBar value={it.targetWeightPct} max={maxWeight} />
                            </div>
                          ) : (
                            <>
                              <span className="font-mono tabular-nums text-kib-cyber">
                                {it.targetWeightPct != null ? `${it.targetWeightPct}%` : '—'}
                                {it.weightPinned ? (
                                  <span className="ml-1 text-[9px] text-amber-200">pinned</span>
                                ) : null}
                              </span>
                              <div className="flex justify-end">
                                <WeightBar value={it.targetWeightPct} max={maxWeight} />
                              </div>
                            </>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {owned ? (
                            <>
                              <p className="font-mono text-kib-fg">{money(it.position?.marketValue)}</p>
                              <p className="text-[10px] text-kib-muted">
                                {it.position?.qty != null ? `${it.position.qty} sh` : ''}
                                {it.position?.avgEntryPrice != null
                                  ? ` @ ${money(it.position.avgEntryPrice)}`
                                  : ''}
                              </p>
                            </>
                          ) : (
                            <span className="text-kib-muted">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono tabular-nums text-kib-muted">
                          {it.actualWeightPct != null ? `${it.actualWeightPct}%` : '—'}
                        </td>
                        <td
                          className={`px-3 py-2.5 text-right font-mono tabular-nums ${pctTone(it.position?.unrealizedPl)}`}
                        >
                          {owned ? (
                            <>
                              <p>{money(it.position?.unrealizedPl)}</p>
                              <p className="text-[10px]">{pct(it.position?.unrealizedPlpc)}</p>
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-xs tabular-nums text-kib-muted">
                          {it.suggestedLimitMin != null && it.suggestedLimitMax != null
                            ? `${money(it.suggestedLimitMin)} – ${money(it.suggestedLimitMax)}`
                            : '—'}
                        </td>
                        <td
                          className="max-w-[200px] truncate px-3 py-2.5 text-xs text-kib-muted"
                          title={it.grokRationale || ''}
                        >
                          {it.grokRationale ||
                            (it.source === 'manual' ? 'Added manually' : it.source || '—')}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <button
                            type="button"
                            className="text-xs text-red-400/90 hover:text-red-300 hover:underline"
                            onClick={() => onRemove(it.userAlertId)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
        {sleeve?.accountError ? (
          <p className="mt-3 text-xs text-amber-200/90">Brokerage: {sleeve.accountError}</p>
        ) : null}
      </div>
    </section>
  );
};
